import { Drawer, Input } from 'antd';
import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Wrapper from './image_selection.styled';
import Button from '../buttons';
import Checkbox from '../checkbox';
import ShowImage from '../show_image';
import { UploadImagesModal } from '../../pages/image_library/modals/upload_images_modal';
import { S3ApiRepository } from '../../data/repositories/s3_repository';
import { ImageType } from '../../data/types/files';
import { toast } from 'react-toastify';
import { Spinner, Tab, Tabs } from '@nextui-org/react';
import {
  actionGetSilalImages,
  GET_SILAL_IMAGES,
  GET_STORE_IMAGES,
} from '../../store/images/image_library_actions';
import { shouldRefreshCache } from '../../utils/functions/caching_functions';

type ImageSelectionProps = {
  showModal: boolean;
  is_private?: boolean;
  setImageModal: (value: boolean) => void;
  updateSelectedImages: (image: ImageType[]) => void;
  minNumberOfImages?: number;
  maxNumberOfImages?: number;
  filesRepository: any;
  store_id?: string;
  s3Repository: S3ApiRepository;
  actionGetStoreImages?: (payload: any) => {
    type: string;
    payload: any;
  };
};

export default function ImageSelection({
  showModal,
  is_private,
  updateSelectedImages,
  minNumberOfImages = 1,
  maxNumberOfImages = 1,
  setImageModal,
  store_id,
  filesRepository,
  s3Repository,
  actionGetStoreImages,
}: ImageSelectionProps) {
  const dispatch = useDispatch();

  const {
    silal_images,
    store_images,
    lastFetchedSilalImages,
    lastFetchedStoreImages,
  } = useSelector((state: any) => state.Images);

  const [galleryImagesSilal, setGalleryImagesSilal] = useState<ImageType[]>([]);
  const [pageSilal, setPageSilal] = useState(1);
  const [totalSilal, setTotalSilal] = useState(0);
  const [prev_pageSilal, setPrevPageSilal] = useState(1);
  const [gallerySilalLoading, setGallerySilalLoading] = useState(false);

  const [galleryImagesStore, setGalleryImagesStore] = useState<ImageType[]>([]);
  const [pageStore, setPageStore] = useState(1);
  const [totalStore, setTotalStore] = useState(0);
  const [prev_pageStore, setPrevPageStore] = useState(1);
  const [galleryStoreLoading, setGalleryStoreLoading] = useState(false);

  const [selectedLibrary, setSelectedLibrary] = useState('silal');

  const [search, setSearch] = useState('');

  const [uploadedNew, setUploadedNew] = useState(false);
  const [skipCacheCheck, setSkipCacheCheck] = useState(true);

  const [selected_image, setSelectedImage] = useState<ImageType[]>([]);

  const loadMoreSilalRef = useRef(null);
  const loadMoreStoreRef = useRef(null);

  const styling = {
    border: '2px solid var(--app-color-dark)',
    background: '#FAFAFA',
    borderRadius: '5px',
  };
  const checkedStyling = {
    border: '2px solid rgb(244, 247, 248)',
    background: '#FAFAFA',
    borderRadius: '5px',
  };

  const removeSelectedImage = (image: ImageType) => {
    const index = selected_image.indexOf(image);
    if (index > -1) {
      selected_image.splice(index, 1);
    }
    setSelectedImage([...selected_image]);
  };

  const handleChange = (image: ImageType) => {
    if (
      minNumberOfImages &&
      maxNumberOfImages &&
      minNumberOfImages !== maxNumberOfImages
    ) {
      if (selected_image.includes(image)) {
        removeSelectedImage(image);
      } else {
        setSelectedImage([...selected_image, image]);
      }
    } else {
      if (selected_image.includes(image)) {
        removeSelectedImage(image);
      } else {
        setSelectedImage([image]);
      }
    }
  };

  useEffect(() => {
    if (showModal) {
      setGallerySilalLoading(true);
      function onSuccess(results: any) {
        setGallerySilalLoading(false);
        if (prev_pageSilal === results.page) {
          setTotalSilal(results.total);
          setGalleryImagesSilal(results.images);
          return;
        } else if (pageSilal === results.page) {
          setTotalSilal(results.total);
          setGalleryImagesSilal(
            (galleryImagesSilal ?? []).concat(results.images),
          );
          setPrevPageSilal(results.page);
        }
        if (!results.files && !results.images) {
          setGalleryImagesSilal(results);
        }
      }
      const payload = {
        data: {
          page: pageSilal,
          limit: 20 * pageSilal,
          query: `*${search}*` || '*',
          is_silal_owned: true,
        },
        success: onSuccess,
      };
      if (
        skipCacheCheck ||
        !lastFetchedSilalImages ||
        shouldRefreshCache(GET_SILAL_IMAGES, lastFetchedSilalImages)
      ) {
        dispatch(actionGetSilalImages(payload));
      } else onSuccess(silal_images);
    }
  }, [pageSilal, uploadedNew, showModal, skipCacheCheck]);

  useEffect(() => {
    if (showModal && actionGetStoreImages) {
      setGalleryStoreLoading(true);
      function onSuccess(results: any) {
        setGalleryStoreLoading(false);
        if (prev_pageStore === results.page) {
          setTotalStore(results.total);
          setGalleryImagesStore(results.files);
          return;
        } else if (pageStore === results.page) {
          setTotalStore(results.total);
          setGalleryImagesStore(
            (galleryImagesStore ?? []).concat(results.files),
          );
          setPrevPageStore(results.page);
        }
        if (!results.files && !results.images) {
          setGalleryImagesStore(results);
        }
      }
      const payload = {
        data: {
          page: pageStore,
          limit: 20 * pageStore,
          query: `*${search}*` || '*',
          is_silal_owned: false,
        },
        success: onSuccess,
      };
      if (
        skipCacheCheck ||
        !lastFetchedStoreImages ||
        shouldRefreshCache(GET_STORE_IMAGES, lastFetchedStoreImages)
      ) {
        dispatch(actionGetStoreImages(payload));
      } else onSuccess(store_images);
    }
  }, [pageStore, uploadedNew, showModal, skipCacheCheck]);

  useEffect(() => {
    if (showModal) {
      const handleSearch = () => {
        if (selectedLibrary === 'silal') {
          setGallerySilalLoading(true);
          const payload = {
            data: {
              page: 1,
              limit: 20,
              query: `*${search}*` || '*',
              is_silal_owned: true,
            },
            success: (results: any) => {
              setGallerySilalLoading(false);
              setGalleryImagesSilal(results.images || []);
              setTotalSilal(results.total || 0);
              setPageSilal(1);
              setPrevPageSilal(1);
            },
          };
          dispatch(actionGetSilalImages(payload));
        }

        if (selectedLibrary === 'store' && actionGetStoreImages) {
          setGalleryStoreLoading(true);
          const payload = {
            data: {
              page: 1,
              limit: 20,
              query: `*${search}*` || '*',
              is_silal_owned: false,
            },
            success: (results: any) => {
              setGalleryStoreLoading(false);
              setGalleryImagesStore(results.files || []);
              setTotalStore(results.total || 0);
              setPageStore(1);
              setPrevPageStore(1);
            },
          };
          dispatch(actionGetStoreImages(payload));
        }
      };
      const debounceTimeout = setTimeout(handleSearch, 500);
      return () => clearTimeout(debounceTimeout);
    }
  }, [search, selectedLibrary, showModal, actionGetStoreImages, dispatch]);

  const loadMoreSilal = () => {
    setPageSilal(pageSilal + 1);
  };

  const loadMoreStore = () => {
    setPageStore(pageStore + 1);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreSilal();
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      },
    );

    if (loadMoreSilalRef.current) {
      observer.observe(loadMoreSilalRef.current);
    }

    return () => {
      if (loadMoreSilalRef.current) {
        observer.unobserve(loadMoreSilalRef.current);
      }
    };
  }, [loadMoreSilalRef]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreSilal();
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      },
    );

    if (loadMoreStoreRef.current) {
      observer.observe(loadMoreStoreRef.current);
    }

    return () => {
      if (loadMoreStoreRef.current) {
        observer.unobserve(loadMoreStoreRef.current);
      }
    };
  }, [loadMoreStoreRef]);

  const handleOk = async () => {
    if (minNumberOfImages && selected_image.length < minNumberOfImages) {
      toast.warn(
        'You need to select at least ' + minNumberOfImages + ' images',
      );
      return;
    }
    if (maxNumberOfImages && selected_image.length > maxNumberOfImages) {
      toast.warn('You can only select ' + maxNumberOfImages + ' images');
      return;
    }

    await updateSelectedImages(selected_image);
    setImageModal(false);
    setSelectedImage([]);
  };

  const handleCancel = () => {
    setImageModal(false);
    setSkipCacheCheck(true);
  };

  const ImageGalleryCardComponent = (image: ImageType, index: number) => {
    return (
      <div key={index} onClick={() => handleChange(image)}>
        <div>
          <div
            style={selected_image.includes(image) ? styling : checkedStyling}
            className="row m-1"
          >
            <div>
              <div
                className={
                  image.alt === undefined ? 'alts-Box my-1 ' : 'alts-Boxs my-1 '
                }
              >
                {image.alt === undefined && (
                  <div className="alts-logo">
                    <p>!</p>
                  </div>
                )}
                <Checkbox
                  key={index}
                  isAlignRight={true}
                  checked={selected_image.includes(image)}
                  onChange={() => handleChange(image)}
                />
              </div>
            </div>
            <div key={index} className="col-lg-12 col-md-12 col-sm-12 my-3">
              <div className="d-flex  justify-content-center  ">
                <ShowImage
                  image={image}
                  key={index}
                  style={{
                    height: '150px',
                    width: '250px',
                    objectFit: 'contain',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Drawer
      title={`Choose ${minNumberOfImages === maxNumberOfImages ? maxNumberOfImages : `${minNumberOfImages} to ${maxNumberOfImages}`} Images`}
      placement="right"
      closable={true}
      onClose={handleCancel}
      open={showModal}
      width={'75%'}
      extra={
        <UploadImagesModal
          postAddAltsCallback={() => {
            setImageModal(true);
            setSkipCacheCheck(true);
          }}
          is_private={is_private}
          getData={() => {
            setSkipCacheCheck(true);
          }}
          store_id={store_id}
          maxImages={maxNumberOfImages}
          s3Repository={s3Repository}
          filesRepository={filesRepository}
        />
      }
      footer={
        <div className="flex ">
          <Button
            text="Clear Selected"
            secondaryButton
            style={{ width: '20%' }}
            onClick={() => {
              setSelectedImage([]);
            }}
          />
          <div style={{ width: '20px' }}></div>
          <Button
            text="Submit"
            style={{ width: '20%', marginLeft: '10px !important' }}
            onClick={handleOk}
          />
        </div>
      }
    >
      <Wrapper>
        <div>
          <div className=" d-flex justify-content-between">
            <Input.Search
              type="text"
              placeholder="Search via alts or IDs"
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            />
          </div>
        </div>
        <Tabs
          className="flex justify-content-center mt-3"
          variant={'light'}
          aria-label="Tabs variants"
          selectedKey={selectedLibrary}
          onSelectionChange={(key) => setSelectedLibrary(key as string)}
        >
          <Tab key="silal" title="Silal Library">
            {gallerySilalLoading ? (
              <Spinner
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              />
            ) : (
              <div className="grid-row">
                {galleryImagesSilal
                  ?.filter((image) => {
                    if (search === '') {
                      return image;
                    } else if (
                      (image.alt !== undefined &&
                        image.alt
                          .toLowerCase()
                          .includes(search.toLowerCase())) ||
                      image?.id?.toString().includes(search.toLowerCase())
                    ) {
                      return image;
                    }
                  })
                  .map(ImageGalleryCardComponent)}
              </div>
            )}
            <div ref={loadMoreSilalRef} className="row load-more">
              {galleryImagesSilal?.length >= totalSilal ? null : (
                <div style={{ textAlign: 'center' }}>
                  <Button
                    text="Load More"
                    style={{
                      width: '20%',
                      marginBottom: '15px',
                    }}
                    onClick={loadMoreSilal}
                  />
                </div>
              )}
            </div>
          </Tab>
          {actionGetStoreImages && (
            <Tab key="store" title="Store Library">
              {galleryStoreLoading ? (
                <Spinner
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                />
              ) : (
                <div className="grid-row">
                  {galleryImagesStore
                    ?.filter((image) => {
                      if (search === '') {
                        return image;
                      } else if (
                        (image.alt !== undefined &&
                          image.alt
                            .toLowerCase()
                            .includes(search.toLowerCase())) ||
                        image?.id?.toString().includes(search.toLowerCase())
                      ) {
                        return image;
                      }
                    })
                    .map(ImageGalleryCardComponent)}
                </div>
              )}
              <div ref={loadMoreStoreRef} className="row load-more">
                {galleryImagesStore?.length >= totalStore ? null : (
                  <div style={{ textAlign: 'center' }}>
                    <Button
                      text="Load More"
                      style={{
                        width: '20%',
                        marginBottom: '15px',
                      }}
                      onClick={loadMoreStore}
                    />
                  </div>
                )}
              </div>
            </Tab>
          )}
        </Tabs>
      </Wrapper>
    </Drawer>
  );
}
